import { useEffect, useState } from "react";

interface MemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
  usagePercent: number;
}

interface PerformanceWithMemory extends Performance {
  memory?: {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
  };
}

export type MemoryWarningLevel = "none" | "warning" | "critical";

export interface MemoryStatus {
  level: MemoryWarningLevel;
  message: string | null;
  info: MemoryInfo | null;
}

// Thresholds for Performance Memory API
const WARNING_THRESHOLD = 0.75; // 75% memory usage
const CRITICAL_THRESHOLD = 0.9; // 90% memory usage

// Fallback thresholds for browsers without Memory API (Firefox, Safari)
// These values are conservative estimates based on typical form memory usage (~50MB per form)
const FALLBACK_WARNING_FORM_COUNT = 20; // Approximately 1GB memory
const FALLBACK_CRITICAL_FORM_COUNT = 30; // Approximately 1.5GB memory

class MemoryMonitor {
  private subscribers: Set<(status: MemoryStatus) => void> = new Set();
  private intervalId: NodeJS.Timeout | null = null;
  private currentStatus: MemoryStatus = {
    level: "none",
    message: null,
    info: null,
  };
  private currentFormCount: number = 0;

  constructor(private checkIntervalMs: number = 5000) {}

  private getMemoryInfo(): MemoryInfo | null {
    const perf = performance as PerformanceWithMemory;

    if (!perf.memory) {
      // Memory API not available (Firefox, Safari)
      return null;
    }

    const { usedJSHeapSize, totalJSHeapSize, jsHeapSizeLimit } = perf.memory;
    const usagePercent = usedJSHeapSize / jsHeapSizeLimit;

    return {
      usedJSHeapSize,
      totalJSHeapSize,
      jsHeapSizeLimit,
      usagePercent,
    };
  }

  private checkMemory(): void {
    const info = this.getMemoryInfo();

    if (!info) {
      // Fallback: Estimate based on form count
      this.currentStatus = this.estimateMemoryByFormCount();
      this.notifySubscribers();
      return;
    }

    let level: MemoryWarningLevel = "none";
    let message: string | null = null;

    if (info.usagePercent >= CRITICAL_THRESHOLD) {
      level = "critical";
      message = `High memory usage (${Math.round(info.usagePercent * 100)}%). Consider closing some posts to prevent browser slowdowns.`;
    } else if (info.usagePercent >= WARNING_THRESHOLD) {
      level = "warning";
      message = `Memory usage is elevated (${Math.round(info.usagePercent * 100)}%). You may experience slowdowns with many posts open.`;
    }

    this.currentStatus = { level, message, info };
    this.notifySubscribers();
  }

  private estimateMemoryByFormCount(): MemoryStatus {
    // Fallback for browsers without memory API
    // Estimate based on form count
    const formCount = this.currentFormCount;

    if (formCount >= FALLBACK_CRITICAL_FORM_COUNT) {
      return {
        level: "critical",
        message: `${formCount} posts open. Consider closing some to prevent browser slowdowns.`,
        info: null,
      };
    } else if (formCount >= FALLBACK_WARNING_FORM_COUNT) {
      return {
        level: "warning",
        message: `${formCount} posts open. You may experience slowdowns with many posts.`,
        info: null,
      };
    }

    return { level: "none", message: null, info: null };
  }

  private notifySubscribers(): void {
    this.subscribers.forEach((callback) => callback(this.currentStatus));
  }

  public start(): void {
    // Prevent multiple intervals - only one monitoring session at a time
    if (this.intervalId) {
      console.warn("[MemoryMonitor] Already monitoring, ignoring start() call");
      return;
    }

    this.checkMemory(); // Immediate check
    this.intervalId = setInterval(
      () => this.checkMemory(),
      this.checkIntervalMs
    );
    console.log("[MemoryMonitor] Started monitoring");
  }

  public stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log("[MemoryMonitor] Stopped monitoring");
    } else {
      console.warn(
        "[MemoryMonitor] Not currently monitoring, ignoring stop() call"
      );
    }
  }

  public subscribe(callback: (status: MemoryStatus) => void): () => void {
    this.subscribers.add(callback);

    // Immediately call with current status
    callback(this.currentStatus);

    // Return unsubscribe function
    return () => {
      this.subscribers.delete(callback);
    };
  }

  public getStatus(): MemoryStatus {
    return this.currentStatus;
  }

  public setFormCount(count: number): void {
    this.currentFormCount = count;
  }
}

// Singleton instance
export const memoryMonitor = new MemoryMonitor(5000);

// React hook for component integration
export const useMemoryStatus = (): MemoryStatus => {
  const [status, setStatus] = useState<MemoryStatus>(memoryMonitor.getStatus());

  useEffect(() => {
    const unsubscribe = memoryMonitor.subscribe(setStatus);
    return unsubscribe;
  }, []);

  return status;
};
